/**
 * F4 Recent entries: the person's most frequent recent entries of this type
 * (from `GET /suggestions`, own data, last 180 days), one tap to fill the
 * form — amount, category, account, remark, payment method. The date stays
 * today; nothing saves until the person does.
 */
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { AmountText } from "@/components/ui/AmountText";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import type { DraftPrefill } from "./draft";

export interface RecentEntry {
  description: string;
  categoryId: string;
  category: { name: string; color?: string | null };
  amountPaise: number;
  accountId: string | null;
  paymentMethod?: string;
  count: number;
  lastDate: string;
}

/** How many one-tap chips to show — enough to cover the daily repeats. */
export const RECENT_LIMIT = 6;

export function useRecentEntries(type: "income" | "expense" | "transfer", enabled: boolean) {
  return useQuery<RecentEntry[]>({
    queryKey: ["suggestions", "recent", type],
    enabled: enabled && type !== "transfer",
    staleTime: 60_000,
    queryFn: async () => {
      const res = await apiFetch(apiUrl("suggestions", "", { type }));
      if (!res.ok) return [];
      // Anything but a list (an error body, a proxy page) must not take the
      // whole entry screen down — the strip simply stays hidden.
      const body: unknown = await res.json().catch(() => null);
      return Array.isArray(body) ? (body as RecentEntry[]) : [];
    },
  });
}

export function recentToPrefill(entry: RecentEntry): DraftPrefill {
  return {
    amountPaise: entry.amountPaise,
    categoryId: entry.categoryId,
    description: entry.description,
    accountId: entry.accountId,
    paymentMethod: entry.paymentMethod ?? null,
  };
}

export function RecentEntries({
  entries,
  onApply,
}: {
  entries: RecentEntry[];
  onApply: (prefill: DraftPrefill) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <div>
      <span className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[.08em] text-[var(--cp-ink-3)]">
        <History className="h-3 w-3" aria-hidden /> Recent
      </span>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Recent entries">
        {entries.slice(0, RECENT_LIMIT).map((entry) => (
          <button
            key={`${entry.description}|${entry.categoryId}`}
            type="button"
            aria-label={`Use recent entry: ${entry.description}`}
            onClick={() => onApply(recentToPrefill(entry))}
            title={`${entry.category.name} · used ${entry.count}×, last ${entry.lastDate}`}
            className="flex max-w-full items-center gap-1.5 rounded-full border border-[var(--cp-line)] bg-[var(--cp-surface)] px-2.5 py-1 text-[12px] text-[var(--cp-ink)] hover:border-[var(--cp-accent)]"
          >
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: entry.category.color ?? "var(--cp-ink-3)" }}
              aria-hidden
            />
            <span className="truncate">{entry.description}</span>
            <AmountText paise={entry.amountPaise} className="shrink-0 text-[var(--cp-ink-3)]" />
          </button>
        ))}
      </div>
    </div>
  );
}
